import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Edit, Plus, Trash2, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Info, Download, Upload, GripVertical, Check, X, ArrowLeft, List as ListIcon, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import ModelWizard from './ModelWizard';
import ModelEditModal from './ModelEditModal';
import CSVImportWizard from './CSVImportWizard';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  default_for_tasks?: string[];
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  date_of_model?: string;
  last_updated?: string;
  normalizedCost?: number;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  functionCalling?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customApiKeyEncrypted?: string;
  customHeaders?: Record<string, string>;
  [key: string]: any;
}

interface ModelItem { modelId: string; rank: number; }
interface ModelList {
  id: string; name: string; description?: string; isActive: boolean;
  items: ModelItem[]; createdAt: string; updatedAt: string;
}

interface BuildingList { id?: string; name: string; desc: string; items: ModelItem[]; }

const MAX_LISTS = 10;

export default function Models() {
  const { user } = useAuth();
  const isAdmin = user?.isAdmin || false;

  // ── Models state ──────────────────────────────────────────────────────────
  const [models, setModels] = useState<AIModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [sortField, setSortField] = useState<string>('ranked_position');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [uniqueCapabilities, setUniqueCapabilities] = useState<string[]>([]);
  const [modelTypeFilter, setModelTypeFilter] = useState<'all' | 'global' | 'custom' | 'privacy'>('all');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; errors: { row: number; message: string }[] } | null>(null);
  const [showCSVWizard, setShowCSVWizard] = useState(false);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // ── Tab + List state ──────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<string>('models');
  const [lists, setLists] = useState<ModelList[]>([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [listSuccess, setListSuccess] = useState('');
  const [listError, setListError] = useState('');

  // ── Builder state ─────────────────────────────────────────────────────────
  const [builderSearch, setBuilderSearch] = useState('');
  const [buildingList, setBuildingList] = useState<BuildingList | null>(null);
  const [builderReturnTab, setBuilderReturnTab] = useState<string>('models');
  const [builderError, setBuilderError] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  // ── Data loading ──────────────────────────────────────────────────────────
  const checkTableScroll = () => {
    const el = tableScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  };

  const scrollTable = (dir: 'left' | 'right') => {
    tableScrollRef.current?.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  const loadModels = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [globalResponse, customResponse] = await Promise.all([
        apiService.get('/portal/models/raw'),
        apiService.get('/portal/models/custom'),
      ]);
      const globalModels = Array.isArray(globalResponse.data) ? globalResponse.data : Array.isArray(globalResponse) ? globalResponse : [];
      const customModels = Array.isArray(customResponse.data) ? customResponse.data : Array.isArray(customResponse) ? customResponse : [];
      const allModels = [...globalModels, ...customModels];
      const capabilitySet = new Set<string>();
      allModels.forEach((model: AIModel) => { if (model.capabilities) Object.keys(model.capabilities).forEach(c => capabilitySet.add(c)); });
      setUniqueCapabilities(Array.from(capabilitySet).sort());
      setModels(allModels.sort((a: AIModel, b: AIModel) => (a.ranked_position || 999) - (b.ranked_position || 999)));
    } catch (err: any) {
      setError(err.message || 'Failed to load models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLists = useCallback(async () => {
    try {
      setListsLoading(true);
      const res = await apiService.getModelLists();
      setLists((res as any).data || res);
    } catch (err: any) {
      setListError(err.message || 'Failed to load lists');
    } finally {
      setListsLoading(false);
    }
  }, []);

  useEffect(() => { loadModels(); }, [loadModels]);
  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => { setListSuccess(''); setListError(''); }, [activeTab]);

  useEffect(() => {
    const t = setTimeout(checkTableScroll, 100);
    return () => clearTimeout(t);
  }, [models, modelTypeFilter]);

  useEffect(() => {
    if (models.length === 0) return;
    setModels(prev => {
      const sorted = [...prev].sort((a, b) => {
        let aVal: any = sortField.startsWith('capability.') ? a.capabilities?.[sortField.replace('capability.', '')] || 0 : a[sortField as keyof AIModel];
        let bVal: any = sortField.startsWith('capability.') ? b.capabilities?.[sortField.replace('capability.', '')] || 0 : b[sortField as keyof AIModel];
        if (aVal === undefined || aVal === null) aVal = sortField === 'ranked_position' ? 999 : '';
        if (bVal === undefined || bVal === null) bVal = sortField === 'ranked_position' ? 999 : '';
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        return sortDirection === 'asc' ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0) : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
      });
      return sorted;
    });
  }, [sortField, sortDirection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Model actions ─────────────────────────────────────────────────────────
  const handleSort = (field: string) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const handleEdit = (model: AIModel) => { setEditingModel(model); setIsCreating(false); setIsModalOpen(true); };
  const handleCreate = () => { setEditingModel(null); setIsCreating(true); setIsModalOpen(true); };

  const handleDelete = async (model: AIModel) => {
    const affectedLists = lists.filter(l => l.items.some(i => i.modelId === model.id));
    const listWarning = affectedLists.length > 0
      ? `\n\nThis model is in ${affectedLists.length} list${affectedLists.length !== 1 ? 's' : ''} (${affectedLists.map(l => `"${l.name}"`).join(', ')}) and will be removed from ${affectedLists.length !== 1 ? 'them' : 'it'} automatically.`
      : '';
    if (!window.confirm(`Delete model "${model.name}"?${listWarning}`)) return;
    try {
      await apiService.delete(`/portal/models/${encodeURIComponent(model.id)}`);
      await loadModels();
      if (affectedLists.length > 0) await loadLists();
    } catch (err: any) { setError(err.message || 'Failed to delete model'); }
  };

  const handleSaveModel = async (model: AIModel): Promise<string | null> => {
    try {
      if (isCreating) await apiService.post('/portal/models', model);
      else await apiService.put(`/portal/models/${encodeURIComponent(model.id)}`, model);
      setIsModalOpen(false);
      await loadModels();
      return null;
    } catch (err: any) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to save model';
      setError(msg);
      alert(`Error: ${msg}`);
      return msg;
    }
  };

  const getApiBaseUrl = () => process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api/v1' : 'http://localhost:5001/api/v1');

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('portal_token');
      const res = await fetch(`${getApiBaseUrl()}/portal/models/csv-template`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Failed to download template');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'custom-models-template.csv'; a.click();
      window.URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message || 'Failed to download template'); }
  };

  const handleCSVImportComplete = async (imported: number, errors: { row: number; message: string }[]) => {
    setShowCSVWizard(false);
    setImportResult({ imported, errors });
    await loadModels();
  };

  const filteredModels = models.filter(m => {
    if (modelTypeFilter === 'global') return m.provider !== 'custom';
    if (modelTypeFilter === 'custom') return m.provider === 'custom';
    if (modelTypeFilter === 'privacy') return m.isPrivacyCompliant === true;
    return true;
  });

  // ── Builder actions ───────────────────────────────────────────────────────
  const openCreateBuilder = () => {
    setBuilderReturnTab(activeTab);
    setBuildingList({ name: '', desc: '', items: [] });
    setBuilderError('');
    setBuilderSearch('');
  };

  const openEditBuilder = (list: ModelList) => {
    setBuilderReturnTab(list.id);
    setBuilderSearch('');
    setBuildingList({
      id: list.id,
      name: list.name,
      desc: list.description || '',
      items: [...list.items].sort((a, b) => a.rank - b.rank),
    });
    setBuilderError('');
  };

  const closeBuilder = () => {
    setBuildingList(null);
    setBuilderError('');
    setSaving(false);
    setActiveTab(builderReturnTab);
  };

  const addToBuilding = (modelId: string) => {
    setBuildingList(prev => {
      if (!prev || prev.items.some(i => i.modelId === modelId)) return prev;
      return { ...prev, items: [...prev.items, { modelId, rank: prev.items.length + 1 }] };
    });
  };

  const removeFromBuilding = (modelId: string) => {
    setBuildingList(prev => {
      if (!prev) return prev;
      const next = prev.items.filter(i => i.modelId !== modelId);
      next.forEach((item, idx) => { item.rank = idx + 1; });
      return { ...prev, items: next };
    });
  };

  const handleDragStart = (idx: number) => setDragIndex(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === idx || !buildingList) return;
    setBuildingList(prev => {
      if (!prev) return prev;
      const next = [...prev.items];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(idx, 0, moved);
      next.forEach((item, i) => { item.rank = i + 1; });
      return { ...prev, items: next };
    });
    setDragIndex(idx);
  };
  const handleDragEnd = () => setDragIndex(null);

  const handleSaveList = async () => {
    if (!buildingList) return;
    if (!buildingList.name.trim()) { setBuilderError('List name is required'); return; }
    if (buildingList.items.length === 0) { setBuilderError('Add at least one model'); return; }
    const hasTD = buildingList.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    if (!hasTD) { setBuilderError('At least one TD model is required'); return; }
    setSaving(true); setBuilderError('');
    try {
      if (buildingList.id) {
        await apiService.updateModelList(buildingList.id, {
          name: buildingList.name.trim(),
          description: buildingList.desc.trim() || undefined,
          items: buildingList.items,
        });
        setListSuccess('Model list updated');
        setBuildingList(null); setBuilderError(''); setSaving(false);
        await loadLists();
        setActiveTab(buildingList.id);
      } else {
        const created = await apiService.createModelList(buildingList.name.trim(), buildingList.desc.trim() || undefined, buildingList.items);
        setListSuccess('Model list created');
        setBuildingList(null); setBuilderError(''); setSaving(false);
        const fresh = await apiService.getModelLists();
        const freshLists: ModelList[] = (fresh as any).data || fresh;
        setLists(freshLists);
        // Navigate to the newly created list's tab
        const newList = freshLists.find(l => l.name === buildingList.name.trim());
        setActiveTab(newList?.id || 'models');
      }
    } catch (err: any) {
      setBuilderError(err.message || 'Failed to save list');
      setSaving(false);
    }
  };

  const handleDeleteList = async (listId: string, listName: string) => {
    if (!window.confirm(`Delete "${listName}"? This will remove it from any API keys that reference it.`)) return;
    try {
      await apiService.deleteModelList(listId);
      setListSuccess('Model list deleted');
      if (activeTab === listId) setActiveTab('models');
      await loadLists();
    } catch (err: any) { setListError(err.message || 'Failed to delete list'); }
  };

  // ── Shared table header ───────────────────────────────────────────────────
  const RANK_W = 80;   // px — must match left-0 + sticky left on Name
  const NAME_W = 200;  // px min-width for Name column

  const SortableHeader = ({ field, children, sticky }: { field: string; children: React.ReactNode; sticky?: 'rank' | 'name' }) => {
    const stickyClasses = sticky === 'rank'
      ? 'sticky left-0 z-10 bg-gray-50'
      : sticky === 'name'
      ? 'sticky z-10 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]'
      : '';
    const stickyStyle: React.CSSProperties = sticky === 'rank'
      ? { width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }
      : sticky === 'name'
      ? { minWidth: NAME_W, left: RANK_W }
      : {};
    return (
      <th
        style={stickyStyle}
        className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none ${stickyClasses}`}
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center space-x-1">
          <span>{children}</span>
          {sortField === field && (sortDirection === 'asc' ? <ChevronUp size={14} className="text-blue-600" /> : <ChevronDown size={14} className="text-blue-600" />)}
        </div>
      </th>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // ── Builder mode (full-page split) ────────────────────────────────────────
  if (buildingList !== null) {
    const hasTD = buildingList.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    const modelMap = new Map(models.map(m => [m.id, m]));
    return (
      <div className="flex flex-col" style={{ height: 'calc(100vh - 6rem)' }}>
        {/* Builder top bar */}
        <div className="flex items-center gap-4 pb-4 flex-shrink-0 flex-wrap">
          <button onClick={closeBuilder} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft size={16} /> Back to lists
          </button>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <input
              type="text"
              placeholder="List name *"
              value={buildingList.name}
              onChange={e => setBuildingList(p => p ? { ...p, name: e.target.value } : p)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500 w-52"
            />
            <input
              type="text"
              placeholder="Description (optional)"
              value={buildingList.desc}
              onChange={e => setBuildingList(p => p ? { ...p, desc: e.target.value } : p)}
              className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-indigo-500 focus:border-indigo-500 w-64"
            />
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {builderError && <span className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">{builderError}</span>}
            {!hasTD && buildingList.items.length > 0 && !builderError && (
              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Add a TD model</span>
            )}
            <button onClick={closeBuilder} className="text-sm text-gray-500 hover:text-gray-800 px-3 py-1.5">Cancel</button>
            <button
              onClick={handleSaveList}
              disabled={saving || !hasTD || buildingList.items.length === 0 || !buildingList.name.trim()}
              className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-1.5 rounded-md text-sm hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" /> : <Check size={14} />}
              {buildingList.id ? 'Save Changes' : 'Create List'}
            </button>
          </div>
        </div>

        {/* Split layout */}
        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left: model table */}
          <div className="flex flex-col flex-1 min-w-0 bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Filter + search bar */}
            <div className="flex items-center gap-2 px-4 py-2 border-b bg-gray-50 flex-shrink-0 flex-wrap">
              <input
                type="text"
                placeholder="Search models..."
                value={builderSearch}
                onChange={e => setBuilderSearch(e.target.value)}
                className="border border-gray-200 rounded-md px-2.5 py-1 text-xs focus:ring-indigo-500 focus:border-indigo-500 w-40 bg-white"
              />
              <div className="w-px h-4 bg-gray-200" />
              {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setModelTypeFilter(f)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md ${
                    modelTypeFilter === f
                      ? f === 'custom' ? 'bg-purple-600 text-white' : f === 'privacy' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </button>
              ))}
              <span className="text-xs text-gray-400 ml-auto">
                {(() => {
                  const q = builderSearch.toLowerCase();
                  return q ? filteredModels.filter(m => m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q)).length : filteredModels.length;
                })()} models · click row to add
              </span>
            </div>

            {/* Table */}
            <div className="overflow-auto flex-1">
              <table className="w-max min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    <SortableHeader field="ranked_position" sticky="rank">Rank</SortableHeader>
                    <SortableHeader field="name" sticky="name">Name</SortableHeader>
                    <SortableHeader field="provider">Provider</SortableHeader>
                    <SortableHeader field="providerModelId">Model ID</SortableHeader>
                    <SortableHeader field="category">Category</SortableHeader>
                    <SortableHeader field="price_per_token">Price</SortableHeader>
                    <SortableHeader field="contextWindow">Context</SortableHeader>
                    <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                    <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                    <SortableHeader field="taskDeterminationModel">TD</SortableHeader>
                    <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                    <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                    <SortableHeader field="supports_vision">Vision</SortableHeader>
                    <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                    {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                    <SortableHeader field="date_of_model">Date</SortableHeader>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 z-10"></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredModels
                    .filter(m => {
                      const q = builderSearch.toLowerCase();
                      return !q || m.name.toLowerCase().includes(q) || m.provider.toLowerCase().includes(q);
                    })
                    .map(model => {
                    const inList = buildingList.items.some(i => i.modelId === model.id);
                    const badge = (val: boolean | undefined, trueColor: string) => (
                      <span className={`inline-block w-4 h-4 rounded-full text-xs leading-4 text-center ${val ? trueColor : 'bg-gray-100 text-gray-400'}`}>
                        {val ? '✓' : ''}
                      </span>
                    );
                    return (
                      <tr
                        key={model.id}
                        onClick={() => !inList && addToBuilding(model.id)}
                        className={`group ${inList ? 'bg-indigo-50' : 'hover:bg-gray-50 cursor-pointer'}`}
                      >
                        <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }} className={`px-3 py-2.5 text-sm text-gray-600 sticky z-10 whitespace-nowrap ${inList ? 'bg-indigo-50' : 'bg-white group-hover:bg-gray-50'}`}>{model.ranked_position || '—'}</td>
                        <td style={{ minWidth: NAME_W, left: RANK_W }} className={`px-3 py-2.5 text-sm font-medium text-gray-900 sticky z-10 whitespace-nowrap truncate shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)] ${inList ? 'bg-indigo-50' : 'bg-white group-hover:bg-gray-50'}`}>
                          {model.name}
                          {model.provider === 'custom' && <span className="ml-1.5 text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Custom</span>}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.provider}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap max-w-[160px] truncate">{model.providerModelId || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.category || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.price_per_token || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.contextWindow ? `${(model.contextWindow / 1000).toFixed(0)}k` : '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.maxTokens ? `${(model.maxTokens / 1000).toFixed(0)}k` : '—'}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.supports_streaming, 'bg-green-100 text-green-700')}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.taskDeterminationModel, 'bg-blue-100 text-blue-700')}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.isPrivacyCompliant, 'bg-green-100 text-green-700')}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.functionCalling, 'bg-indigo-100 text-indigo-700')}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.supports_vision, 'bg-green-100 text-green-700')}</td>
                        <td className="px-3 py-2.5 text-center whitespace-nowrap">{badge(model.isUncensored, 'bg-purple-100 text-purple-700')}</td>
                        {uniqueCapabilities.map(c => (
                          <td key={c} className="px-3 py-2.5 text-center whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${model.capabilities?.[c] ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`}>{model.capabilities?.[c] || '—'}</span>
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">{model.date_of_model || '—'}</td>
                        <td className={`px-3 py-2.5 sticky right-0 z-10 text-center w-10 ${inList ? 'bg-indigo-50' : 'bg-white group-hover:bg-gray-50'}`}>
                          {inList
                            ? <Check size={14} className="text-indigo-500 mx-auto" />
                            : <Plus size={14} className="text-gray-300 mx-auto" />
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right: list panel */}
          <div className="w-72 flex-shrink-0 flex flex-col bg-white rounded-lg border-2 border-indigo-200 overflow-hidden">
            <div className="px-4 py-3 border-b bg-indigo-50 flex-shrink-0">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-indigo-700">In list</h3>
                <span className="text-xs text-indigo-400">{buildingList.items.length} model{buildingList.items.length !== 1 ? 's' : ''}</span>
              </div>
              <p className="text-xs text-indigo-400 mt-0.5">Drag to reorder · rank 1 = highest priority</p>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {buildingList.items.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-8">Click a model from the table to add it</p>
              ) : (
                buildingList.items.map((item, idx) => {
                  const model = modelMap.get(item.modelId);
                  return (
                    <div
                      key={item.modelId}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragOver={e => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      className={`flex items-center gap-2 border rounded px-2 py-1.5 bg-white cursor-grab select-none ${
                        dragIndex === idx ? 'opacity-50 border-indigo-400' : 'border-indigo-100 hover:border-indigo-300'
                      }`}
                    >
                      <GripVertical size={13} className="text-indigo-200 flex-shrink-0" />
                      <span className="text-xs font-mono text-indigo-300 w-4 text-right flex-shrink-0">{item.rank}</span>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-gray-800 block truncate">{model?.name || item.modelId}</span>
                        <div className="flex gap-1 mt-0.5">
                          {model?.taskDeterminationModel && <span className="text-xs bg-blue-100 text-blue-600 px-1 rounded">TD</span>}
                          {model?.isPrivacyCompliant && <span className="text-xs bg-green-100 text-green-600 px-1 rounded">P</span>}
                        </div>
                      </div>
                      <button onClick={() => removeFromBuilding(item.modelId)} className="text-indigo-200 hover:text-red-500 flex-shrink-0">
                        <X size={13} />
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── List health checks ────────────────────────────────────────────────────
  const getListWarnings = (list: ModelList) => {
    const hasTD = list.items.some(i => models.find(m => m.id === i.modelId)?.taskDeterminationModel);
    const hasPrivacyTD = list.items.some(i => {
      const m = models.find(mo => mo.id === i.modelId);
      return m?.taskDeterminationModel && m?.isPrivacyCompliant;
    });
    const warnings: string[] = [];
    if (!hasTD) warnings.push('no-td');
    if (!hasPrivacyTD) warnings.push('no-privacy-td');
    return warnings;
  };

  // ── Normal page ───────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Row 1: Title + tabs (full width, no competition) */}
      <div className="border-b border-gray-200">
        <div className="flex items-end gap-0 overflow-x-auto">
          <h1 className="text-2xl font-bold text-gray-900 pb-3 pr-6 flex-shrink-0">Models</h1>
          <button
            onClick={() => setActiveTab('models')}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 flex-shrink-0 ${activeTab === 'models' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
          >
            All Models
          </button>
          {lists.map(list => {
            const warnings = getListWarnings(list);
            const hasIssue = warnings.length > 0;
            return (
              <button
                key={list.id}
                onClick={() => setActiveTab(list.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 flex-shrink-0 flex items-center gap-1.5 ${activeTab === list.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                title={list.name}
              >
                <span className="max-w-[120px] truncate">{list.name}</span>
                {hasIssue && <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />}
              </button>
            );
          })}
          {lists.length < MAX_LISTS && (
            <button
              onClick={openCreateBuilder}
              className="px-4 py-2.5 text-sm font-medium border-b-2 border-transparent text-gray-400 hover:text-indigo-600 flex-shrink-0 flex items-center gap-1"
            >
              <Plus size={14} /> Create List
            </button>
          )}
        </div>
      </div>


      {/* ── All Models tab ── */}
      {activeTab === 'models' && (
        <>
          {error && <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}
          {importResult && (
            <div className="p-4 bg-green-50 border border-green-300 rounded">
              <div className="flex items-center justify-between">
                <p className="text-sm text-green-800 font-medium">
                  Imported {importResult.imported} model{importResult.imported !== 1 ? 's' : ''}.
                  {importResult.errors.length > 0 && ` ${importResult.errors.length} row${importResult.errors.length !== 1 ? 's' : ''} had errors.`}
                </p>
                <button onClick={() => setImportResult(null)} className="text-green-600 hover:text-green-800 text-sm">Dismiss</button>
              </div>
              {importResult.errors.length > 0 && (
                <ul className="mt-2 text-xs text-red-700 list-disc list-inside">
                  {importResult.errors.map((e, i) => <li key={i}>Row {e.row}: {e.message}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Filters + actions row */}
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-sm font-medium text-gray-700">Filter:</span>
            {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
              <button
                key={f}
                onClick={() => setModelTypeFilter(f)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                  modelTypeFilter === f
                    ? f === 'custom' ? 'bg-purple-600 text-white' : f === 'privacy' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {f === 'all' ? 'All' : f === 'global' ? 'Global' : f === 'custom' ? 'Custom' : 'Privacy'}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setShowConfigModal(true)}
                className="p-1.5 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                title="Model configuration requirements"
              >
                <Info size={16} />
              </button>
              <button onClick={handleDownloadTemplate} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center gap-2 text-sm">
                <Download size={16} /> Template
              </button>
              <button onClick={() => setShowCSVWizard(true)} className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center gap-2 text-sm">
                <Upload size={16} /> Import CSV
              </button>
              <button onClick={handleCreate} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2 text-sm">
                <Plus size={16} /> Add Model
              </button>
            </div>
          </div>

          {/* No-lists nudge */}
          {lists.length === 0 && !listsLoading && (
            <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3">
              <p className="text-sm text-indigo-700">
                <span className="font-medium">Tip:</span> Create a model list to restrict which models an API key can use — great for privacy, cost control, or custom routing.
              </p>
              <button onClick={openCreateBuilder} className="ml-4 flex-shrink-0 text-sm font-medium text-indigo-600 hover:text-indigo-800 underline underline-offset-2">
                Create your first list →
              </button>
            </div>
          )}

          {/* Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-400">
                Sorted by <span className="font-medium text-gray-500">{sortField.startsWith('capability.') ? sortField.replace('capability.', '') : sortField.replace(/_/g, ' ')}</span> {sortDirection === 'asc' ? '↑' : '↓'} · {filteredModels.length} of {models.length} models
              </span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-400 mr-1">Scroll</span>
                <button onClick={() => scrollTable('left')} disabled={!canScrollLeft} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronLeft size={16} className="text-gray-500" /></button>
                <button onClick={() => scrollTable('right')} disabled={!canScrollRight} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronRight size={16} className="text-gray-500" /></button>
              </div>
            </div>
            <div ref={tableScrollRef} onScroll={checkTableScroll} className="overflow-x-auto max-h-[800px] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 relative">
                <thead className="bg-gray-50 sticky top-0 z-20">
                  <tr>
                    <SortableHeader field="ranked_position" sticky="rank">Rank</SortableHeader>
                    <SortableHeader field="name" sticky="name">Name</SortableHeader>
                    <SortableHeader field="provider">Provider</SortableHeader>
                    <SortableHeader field="providerModelId">Model ID</SortableHeader>
                    <SortableHeader field="price_per_token">Price</SortableHeader>
                    <SortableHeader field="category">Category</SortableHeader>
                    <SortableHeader field="contextWindow">Context Window</SortableHeader>
                    <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                    <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                    {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                    <SortableHeader field="supports_vision">Vision</SortableHeader>
                    <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                    <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                    <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                    <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                    <SortableHeader field="date_of_model">Date</SortableHeader>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 z-10 bg-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredModels.map(model => (
                    <tr key={model.id} className="group hover:bg-gray-50">
                      <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }} className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 font-medium sticky z-10 bg-white group-hover:bg-gray-50">{model.ranked_position || 'N/A'}</td>
                      <td style={{ minWidth: NAME_W, left: RANK_W }} className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 truncate sticky z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                        {model.name}
                        {model.provider === 'custom' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Custom</span>}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.provider}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">{model.providerModelId}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.price_per_token || 'N/A'}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.category || 'N/A'}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.contextWindow || 'N/A'}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.maxTokens || 'N/A'}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.supports_streaming ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.supports_streaming ? '✓' : '✗'}</span>
                      </td>
                      {uniqueCapabilities.map(c => (
                        <td key={c} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${model.capabilities?.[c] ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`}>{model.capabilities?.[c] || '-'}</span>
                        </td>
                      ))}
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.supports_vision ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.supports_vision ? '✓' : '✗'}</span></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.isUncensored ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>{model.isUncensored ? '✓' : '✗'}</span></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.isPrivacyCompliant ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{model.isPrivacyCompliant ? '✓' : '✗'}</span></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.functionCalling ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`}>{model.functionCalling ? '✓' : '✗'}</span></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.taskDeterminationModel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.taskDeterminationModel ? '✓' : '✗'}</span></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.date_of_model || 'N/A'}</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 sticky right-0 z-10 bg-white group-hover:bg-gray-50 shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                        <div className="flex space-x-2">
                          {(isAdmin || model.isUserCustom) && <button onClick={() => handleEdit(model)} className="text-blue-600 hover:text-blue-900"><Edit size={16} /></button>}
                          {(isAdmin || model.isUserCustom) && <button onClick={() => handleDelete(model)} className="text-red-600 hover:text-red-900"><Trash2 size={16} /></button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── List tab view ── */}
      {activeTab !== 'models' && (() => {
        const activeList = lists.find(l => l.id === activeTab);
        if (!activeList) return (
          <div className="text-center py-16 text-gray-400 text-sm">List not found.</div>
        );
        const rankMap = new Map(activeList.items.map(i => [i.modelId, i.rank]));
        const listModels = models
          .filter(m => {
            if (!rankMap.has(m.id)) return false;
            if (modelTypeFilter === 'global') return m.provider !== 'custom';
            if (modelTypeFilter === 'custom') return m.provider === 'custom';
            if (modelTypeFilter === 'privacy') return m.isPrivacyCompliant === true;
            return true;
          })
          .sort((a, b) => {
            // When user hasn't changed sort (or clicked List Rank), sort by list rank
            if (sortField === 'ranked_position') {
              const diff = (rankMap.get(a.id) ?? 999) - (rankMap.get(b.id) ?? 999);
              return sortDirection === 'asc' ? diff : -diff;
            }
            // Otherwise models array is already sorted by handleSort — preserve that order
            return 0;
          });

        return (
          <div className="space-y-4">
            {listError && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex justify-between">{listError}<button onClick={() => setListError('')}><X size={14} /></button></div>}
            {listSuccess && <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex justify-between">{listSuccess}<button onClick={() => setListSuccess('')}><X size={14} /></button></div>}

            {/* Health warnings */}
            {(() => {
              const warnings = getListWarnings(activeList);
              const noTD = warnings.includes('no-td');
              const noPrivacyTD = warnings.includes('no-privacy-td');
              if (!noTD && !noPrivacyTD) return null;
              return (
                <div className="space-y-2">
                  {noTD && (
                    <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-md">
                      <AlertTriangle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-700">No task determination model in this list</p>
                        <p className="text-xs text-red-600 mt-0.5">Task determination will fail for any API key using this list. Add at least one TD-enabled model to resolve this.</p>
                      </div>
                    </div>
                  )}
                  {noPrivacyTD && (
                    <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-700">No privacy-compliant TD model in this list</p>
                        <p className="text-xs text-amber-600 mt-0.5">Without a model that is both TD-enabled and privacy-compliant, task determination for privacy-mode requests may fall back to a non-private model. Consider adding one to keep the full request chain compliant.</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* List subtitle */}
            {activeList.description && (
              <p className="text-sm text-gray-500 -mt-2"><span className="font-medium text-gray-600">Description:</span> {activeList.description}</p>
            )}

            {/* Filters + actions row */}
            <div className="flex gap-2 items-center flex-wrap">
              <span className="text-sm font-medium text-gray-700">Filter:</span>
              {(['all', 'global', 'custom', 'privacy'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setModelTypeFilter(f)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md ${
                    modelTypeFilter === f
                      ? f === 'custom' ? 'bg-purple-600 text-white' : f === 'privacy' ? 'bg-green-600 text-white' : 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {f === 'all' ? 'All' : f === 'global' ? 'Global' : f === 'custom' ? 'Custom' : 'Privacy'}
                </button>
              ))}
              <div className="ml-auto flex items-center gap-2">
                <span className="text-xs text-gray-400">{listModels.length} model{listModels.length !== 1 ? 's' : ''}</span>
                <button
                  onClick={() => openEditBuilder(activeList)}
                  className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-4 py-2 rounded-md hover:bg-indigo-100 flex items-center gap-2 text-sm"
                >
                  <Edit size={16} /> Edit List
                </button>
                <button
                  onClick={() => handleDeleteList(activeList.id, activeList.name)}
                  className="bg-red-50 text-red-700 border border-red-200 px-4 py-2 rounded-md hover:bg-red-100 flex items-center gap-2 text-sm"
                >
                  <Trash2 size={16} /> Delete
                </button>
              </div>
            </div>

            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto max-h-[800px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200 relative">
                  <thead className="bg-gray-50 sticky top-0 z-20">
                    <tr>
                      <SortableHeader field="ranked_position" sticky="rank"><span className="text-indigo-500">List Rank</span></SortableHeader>
                      <SortableHeader field="name" sticky="name">Name</SortableHeader>
                      <SortableHeader field="provider">Provider</SortableHeader>
                      <SortableHeader field="providerModelId">Model ID</SortableHeader>
                      <SortableHeader field="price_per_token">Price</SortableHeader>
                      <SortableHeader field="category">Category</SortableHeader>
                      <SortableHeader field="contextWindow">Context Window</SortableHeader>
                      <SortableHeader field="maxTokens">Max Tokens</SortableHeader>
                      <SortableHeader field="supports_streaming">Streaming</SortableHeader>
                      {uniqueCapabilities.map(c => <SortableHeader key={c} field={`capability.${c}`}>{c.charAt(0).toUpperCase() + c.slice(1)}</SortableHeader>)}
                      <SortableHeader field="supports_vision">Vision</SortableHeader>
                      <SortableHeader field="isUncensored">Uncensored</SortableHeader>
                      <SortableHeader field="isPrivacyCompliant">Privacy</SortableHeader>
                      <SortableHeader field="functionCalling">Fn Calling</SortableHeader>
                      <SortableHeader field="taskDeterminationModel">Task Det.</SortableHeader>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {listModels.map(model => (
                      <tr key={model.id} className="group hover:bg-gray-50">
                        <td style={{ width: RANK_W, minWidth: RANK_W, maxWidth: RANK_W, left: 0 }} className="px-3 py-4 whitespace-nowrap text-sm font-semibold text-indigo-600 sticky z-10 bg-white group-hover:bg-gray-50">{rankMap.get(model.id)}</td>
                        <td style={{ minWidth: NAME_W, left: RANK_W }} className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900 truncate sticky z-10 bg-white group-hover:bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]">
                          {model.name}
                          {model.provider === 'custom' && <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">Custom</span>}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.provider}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 max-w-xs truncate">{model.providerModelId}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.price_per_token || 'N/A'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.category || 'N/A'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.contextWindow || 'N/A'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{model.maxTokens || 'N/A'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.supports_streaming ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.supports_streaming ? '✓' : '✗'}</span></td>
                        {uniqueCapabilities.map(c => (
                          <td key={c} className="px-3 py-4 whitespace-nowrap text-sm text-center">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold ${model.capabilities?.[c] ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`}>{model.capabilities?.[c] || '-'}</span>
                          </td>
                        ))}
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.supports_vision ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.supports_vision ? '✓' : '✗'}</span></td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.isUncensored ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>{model.isUncensored ? '✓' : '✗'}</span></td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.isPrivacyCompliant ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>{model.isPrivacyCompliant ? '✓' : '✗'}</span></td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.functionCalling ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-800'}`}>{model.functionCalling ? '✓' : '✗'}</span></td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500"><span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${model.taskDeterminationModel ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{model.taskDeterminationModel ? '✓' : '✗'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Model modals */}
      {isCreating ? (
        <ModelWizard isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveModel} isAdmin={isAdmin} existingModels={models} />
      ) : (
        <ModelEditModal model={editingModel} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSave={handleSaveModel} isAdmin={isAdmin} existingModels={models} />
      )}
      <CSVImportWizard isOpen={showCSVWizard} onClose={() => setShowCSVWizard(false)} onImportComplete={handleCSVImportComplete} existingModels={models} isAdmin={isAdmin} />

      {/* Config requirements modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] !m-0 p-4" onClick={() => setShowConfigModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900">Model Configuration Requirements</h3>
              <button onClick={() => setShowConfigModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 overflow-y-auto flex-1 space-y-5 text-sm text-gray-700">
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Task Determination (TD) Models</h4>
                <p className="text-gray-600">At least one model with <span className="font-medium">Task Determination</span> enabled must be included in every model list. TD models are used to classify incoming requests before routing them to the appropriate model.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Privacy-Compliant Models</h4>
                <p className="text-gray-600">If an API key uses <span className="font-medium">Privacy-Compliant mode</span>, at least one privacy-compliant model must exist in the list. Requests will fail if no privacy model is available.</p>
                <p className="text-gray-600 mt-2">For best results, include a model that is <span className="font-medium">both Privacy-Compliant and TD-enabled</span>. When the router performs task determination, it will use a privacy-safe model if one is available — keeping the entire request chain compliant. Without one, task determination may fall back to a non-privacy model even if the final response model is compliant.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Custom Endpoint Models</h4>
                <p className="text-gray-600">Custom models require a valid <span className="font-medium">endpoint URL</span> and optionally a custom API key. These are only available when the API key's model selection mode is set to <em>All</em> or <em>Custom Only</em>.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Model List Limits</h4>
                <p className="text-gray-600">You can create up to <span className="font-medium">{MAX_LISTS} model lists</span>. Each list must contain at least one model and one TD model.</p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">List Rank</h4>
                <p className="text-gray-600">The order of models in a list determines their <span className="font-medium">priority</span>. Rank 1 is the highest priority and will be selected first when the API key prioritises by rank. When prioritising by price, the cheapest eligible model is selected regardless of rank.</p>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex-shrink-0 flex justify-end">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
